<template>
  <div class="tcv-wrap">

    <!-- Header: today label + month navigation -->
    <div class="tcv-header">
      <div class="tcv-today-label">
        <fa-icon :icon="['fas', 'calendar-alt']" class="tcv-today-icon" />
        Today: {{ todayLabel }}
      </div>
      <div class="tcv-nav">
        <button class="tcv-nav-btn" :disabled="calendarMonth === 1" title="Previous month" @click="$emit('prev-month')">
          <fa-icon :icon="['fas', 'chevron-left']" />
        </button>
        <span class="tcv-nav-month">{{ currentMonthLabel }}</span>
        <button class="tcv-nav-btn" :disabled="calendarMonth === 12" title="Next month" @click="$emit('next-month')">
          <fa-icon :icon="['fas', 'chevron-right']" />
        </button>
      </div>
    </div>

    <!-- Month bar: Jan–Dec quick-jump -->
    <div class="tcv-month-bar" ref="monthBar">
      <span class="tcv-month-slider" :style="sliderStyle"></span>
      <button
        v-for="(name, i) in monthNames"
        :key="i"
        class="tcv-month-btn"
        :class="{ 'is-active': calendarMonth === i + 1 }"
        @click="$emit('set-month', i + 1)"
      >{{ name }}</button>
    </div>

    <!-- Calendar grid -->
    <div class="tcv-grid-wrap">
      <!-- Weekday header: M T W T F S S -->
      <div class="tcv-weekdays">
        <div v-for="(d, i) in weekDayLabels" :key="i" class="tcv-weekday">{{ d }}</div>
      </div>

      <!-- Day cells -->
      <div class="tcv-days">
        <!-- Leading blanks before the 1st -->
        <div v-for="n in leadingBlanks" :key="'blank-' + n" class="tcv-day tcv-day--blank" />

        <!-- Actual days -->
        <div
          v-for="day in daysInMonth"
          :key="'day-' + day"
          class="tcv-day"
          :class="{
            'tcv-day--today': isTodayDay(day),
            'tcv-day--has-items': dayItems(day).length > 0,
            'tcv-day--weekend': isWeekend(day)
          }"
        >
          <span class="tcv-day-num" :class="{ 'tcv-day-num--today': isTodayDay(day) }">{{ day }}</span>

          <div class="tcv-day-items">
            <app-tooltip :text="item.client.client" position="top" truncate
              v-for="item in dayItems(day)"
              :key="item._key"
              class="tcv-item w-full"
              :class="deadlineClass(item.dl, calendarMonth)"
              :title="item.client.client"
            >
              <!-- Client initial — tooltip via :title on parent for the whole chip -->
              <div class="tcv-item-initial">{{ clientInitialDisplay(item.client) }}</div>
              <!-- Deadline name -->
              <div class="tcv-item-name">{{ item.dl.display_name }}</div>
              <!-- Status text -->
              <div
                v-if="showStatusFor(item.dl)"
                class="tcv-item-status"
                :class="{ 'tcv-item-status--overdue': isOverdue(item.dl), 'tcv-item-status--completed': item.dl.status === 'completed' }"
              >{{ item.dl.status === 'completed' ? 'Completed' : 'Pending' }}</div>

              <!-- Spinner while updating -->
              <fa-icon
                v-if="item.dl.is_adhoc ? updatingAdhoc[item.dl.id] : updatingTask[item._key]"
                :icon="['fas', 'spinner']"
                spin
                class="tcv-item-spinner"
              />
              <!-- Invisible overlay trigger to open the custom status dropdown -->
              <div
                v-else
                class="tcv-item-trigger"
                @click.stop="openTcvDropdown(item, $event)"
              ></div>
            </app-tooltip>
          </div>
        </div>
      </div>
    </div>

    <!--
      Portalled status dropdown — rendered here (Vue reactivity), moved to <body>
      in mounted() so position:fixed is always viewport-relative. One shared
      instance repositioned on each chip click. Uses ap-si-menu/ap-si-opt from
      _select-input.scss; tcv-dl-dropdown-menu is a scoped width override only.
    -->
    <div
      ref="tcvDlMenu"
      class="ap-si-menu tcv-dl-dropdown-menu"
      v-show="!!openDropdownKey"
      :style="dropdownStyle"
      @click.stop
    >
      <div
        class="ap-si-opt"
        :class="{ 'is-active': openDropdownDl && openDropdownDl.status !== 'completed' }"
        @click="selectTcvStatus('pending')"
      >
        <span class="ap-si-opt-label">Pending</span>
        <fa-icon
          v-if="openDropdownDl && openDropdownDl.status !== 'completed'"
          :icon="['fas', 'check']"
          class="ap-si-opt-check"
        />
      </div>
      <div
        class="ap-si-opt"
        :class="{ 'is-active': openDropdownDl && openDropdownDl.status === 'completed' }"
        @click="selectTcvStatus('completed')"
      >
        <span class="ap-si-opt-label">Completed</span>
        <fa-icon
          v-if="openDropdownDl && openDropdownDl.status === 'completed'"
          :icon="['fas', 'check']"
          class="ap-si-opt-check"
        />
      </div>
    </div>

  </div>
</template>

<script>
import AppTooltip from './AppTooltip.vue'
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export default {
  name: 'task-calendar-view',
  components: { AppTooltip },
  props: {
    clients: { type: Array, default: () => [] },
    calendarMonth: { type: Number, required: true },
    calendarYear: { type: Number, required: true },
    currentMonth: { type: Number, required: true },
    currentYear: { type: Number, required: true },
    selectedSlot: { type: String, default: 'current' },
    monthNames: { type: Array, default: () => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] },
    updatingTask: { type: Object, default: () => ({}) },
    updatingAdhoc: { type: Object, default: () => ({}) },
    getDeadlines: { type: Function, required: true },
    deadlineClass: { type: Function, required: true }
  },
  data () {
    return {
      weekDayLabels: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
      sliderStyle: {},
      // Status dropdown (portal)
      openDropdownKey: null,
      openDropdownItem: null,
      openDropdownDl: null,
      dropdownStyle: {}
    }
  },
  mounted () {
    this.$nextTick(this.updateSlider)
    // Portal: move status dropdown to <body> so position:fixed is viewport-relative
    document.body.appendChild(this.$refs.tcvDlMenu)
    document.addEventListener('click', this.onTcvOutsideClick)
    window.addEventListener('scroll', this.onTcvScroll, true)
    window.addEventListener('resize', this.closeTcvDropdown)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onTcvOutsideClick)
    window.removeEventListener('scroll', this.onTcvScroll, true)
    window.removeEventListener('resize', this.closeTcvDropdown)
    const menu = this.$refs.tcvDlMenu
    if (menu && menu.parentNode === document.body) {
      document.body.removeChild(menu)
    }
  },
  watch: {
    calendarMonth () { this.$nextTick(this.updateSlider) }
  },
  computed: {
    todayLabel () {
      const now = new Date()
      return `${WEEKDAYS[now.getDay()]} ${now.getDate()} ${FULL_MONTHS[now.getMonth()]} ${now.getFullYear()}`
    },
    currentMonthLabel () {
      return `${FULL_MONTHS[this.calendarMonth - 1]} ${this.calendarYear}`
    },
    daysInMonth () {
      return new Date(this.calendarYear, this.calendarMonth, 0).getDate()
    },
    leadingBlanks () {
      // JS getDay(): 0=Sun … 6=Sat. Convert to Mon-first: Mon=0 … Sun=6
      const firstDay = new Date(this.calendarYear, this.calendarMonth - 1, 1).getDay()
      return (firstDay + 6) % 7
    }
  },
  methods: {
    updateSlider () {
      const bar = this.$refs.monthBar
      const btn = bar && bar.querySelector('.tcv-month-btn.is-active')
      if (!btn) return
      this.sliderStyle = { left: btn.offsetLeft + 'px', width: btn.offsetWidth + 'px' }
    },
    isTodayDay (day) {
      const now = new Date()
      return (
        this.selectedSlot === 'current' &&
        this.calendarMonth === now.getMonth() + 1 &&
        this.calendarYear === now.getFullYear() &&
        day === now.getDate()
      )
    },
    clientInitialDisplay (client) {
      return (client.initial || client.client || '?').slice(0, 4)
    },
    dayItems (day) {
      const lastDay = this.daysInMonth
      const items = []
      for (const client of this.clients) {
        const dls = this.getDeadlines(client, this.calendarMonth)
        for (const dl of dls) {
          if (!dl.date || dl.date.day == null) continue
          // Clamp month-end deadlines (e.g. day:31 in a 30-day month) to actual last day
          const targetDay = dl.date.day > lastDay ? lastDay : dl.date.day
          if (targetDay !== day) continue
          const key = dl.is_adhoc
            ? `adhoc-${dl.id}`
            : `${client.id}-${dl.name}${dl._month != null ? '-' + dl._month : ''}`
          items.push({ client, dl, _key: key })
        }
      }
      return items
    },
    showStatusFor (dl) {
      if (this.selectedSlot === 'prev') return true
      if (this.selectedSlot === 'current' && this.calendarMonth <= this.currentMonth) return true
      return dl.status === 'completed'
    },
    isOverdue (dl) {
      if (dl.status !== 'pending') return false
      if (this.selectedSlot === 'prev') return true
      if (this.selectedSlot === 'current') return this.calendarMonth < this.currentMonth
      return false
    },
    isWeekend (day) {
      // Mon-first grid: position 0=Mon … 5=Sat, 6=Sun
      const pos = (this.leadingBlanks + day - 1) % 7
      return pos === 5 || pos === 6
    },
    // ── Status dropdown (portal) ─────────────────────────────────────────────
    openTcvDropdown (item, e) {
      // Toggle closed if the same chip is clicked again
      if (this.openDropdownKey === item._key) {
        this.closeTcvDropdown()
        return
      }
      this.openDropdownKey = item._key
      this.openDropdownItem = item
      this.openDropdownDl = item.dl
      this._tcvTriggerEl = e.currentTarget
      this.$nextTick(this.positionTcvDropdown)
    },
    positionTcvDropdown () {
      if (!this._tcvTriggerEl) return
      const r = this._tcvTriggerEl.getBoundingClientRect()
      this.dropdownStyle = {
        position: 'fixed',
        top: (r.bottom + 4) + 'px',
        left: r.left + 'px',
        zIndex: 10000
      }
    },
    closeTcvDropdown () {
      this.openDropdownKey = null
      this.openDropdownItem = null
      this.openDropdownDl = null
      this._tcvTriggerEl = null
    },
    onTcvOutsideClick (e) {
      const menu = this.$refs.tcvDlMenu
      if (menu && menu.contains(e.target)) return
      if (this.openDropdownKey) this.closeTcvDropdown()
    },
    onTcvScroll () {
      if (this.openDropdownKey) this.positionTcvDropdown()
    },
    selectTcvStatus (status) {
      if (!this.openDropdownItem) return
      const { client, dl } = this.openDropdownItem
      this.closeTcvDropdown()
      this.$emit('update-status', client, dl, status)
    }
    // ────────────────────────────────────────────────────────────────────────
  }
}
</script>
